/**
 * PostgREST plafonne chaque réponse à 1000 lignes et les URL trop longues
 * (gros filtres `.in()`) sont rejetées en 400 : ces helpers découpent les
 * requêtes en lots et parcourent les pages.
 */
export const POSTGREST_PAGE_SIZE = 1000;
// ~100 UUID par filtre `in.(...)` ≈ 4 Ko d'URL, bien sous les limites du proxy.
export const IN_FILTER_CHUNK_SIZE = 100;

type PageResult<T> = { data: T[] | null; error: unknown };

export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

/** Charge toutes les pages d'une requête ; `fetchPage` doit appliquer `.range(from, to)` avec un tri stable. */
export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize = POSTGREST_PAGE_SIZE
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) throw error;
    const page = data ?? [];
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
}

/** Exécute une requête par lot d'identifiants (filtre `.in()`) et concatène toutes les pages. */
export async function fetchAllByIds<T>(
  ids: string[],
  fetchPage: (idsChunk: string[], from: number, to: number) => PromiseLike<PageResult<T>>,
  chunkSize = IN_FILTER_CHUNK_SIZE
): Promise<T[]> {
  const results = await Promise.all(
    chunk(ids, chunkSize).map((idsChunk) => fetchAllPages<T>((from, to) => fetchPage(idsChunk, from, to)))
  );
  return results.flat();
}
